/**
 * Home.
 *
 * The app used to open straight onto three tabs, which is fine when there are
 * three things and wrong once there are six. Home exists to answer one question
 * — where was I, and where can I go — and then get out of the way.
 *
 * Deliberately not a dashboard. Counting how many minutes somebody practised
 * turns a workbook into a performance review, and the brief rules it out. The
 * only history shown is the piece of work they had open, because that is the
 * thing they came back for.
 */

import { useEffect, useState } from 'react';
import { Icon, type IconName } from '../design/Icon';
import { repositories } from '../storage/repositories';
import type { Destination } from '../auth/routes';

export interface HomeProps {
  go: (destination: Destination) => void;
  /** Opens the most recent document straight onto the staff. */
  onContinue: (documentId: string) => void;
}

interface RecentWork {
  id: string;
  title: string;
  updatedAt: number;
}

const CARDS: { destination: Destination; icon: IconName; title: string; blurb: string }[] = [
  {
    destination: 'fib',
    icon: 'plus',
    title: 'Chord FIB',
    blurb: 'Build a chord from one note you know, or practise filling in the blanks.',
  },
  {
    destination: 'modes',
    icon: 'play',
    title: 'Modes Lab',
    blurb: 'Hear what makes each mode itself, over a held tonic.',
  },
  {
    destination: 'intervals',
    icon: 'book',
    title: 'Intervals Lab',
    blurb: 'See, count, and hear the space between two notes.',
  },
  {
    destination: 'chord-legend',
    icon: 'book',
    title: 'Chord Legend',
    blurb: 'Every chord formula in one place. 1–3–5, 1–♭3–5, and the rest.',
  },
  {
    destination: 'learn',
    icon: 'book',
    title: 'Learn',
    blurb: 'Twelve units, cause first — each ending in writing, hearing and using the idea.',
  },
  {
    destination: 'classroom',
    icon: 'classroom',
    title: 'Class',
    blurb: 'Join a class, and choose which work your teacher can see.',
  },
];

export function Home({ go, onContinue }: HomeProps) {
  const [recent, setRecent] = useState<RecentWork | null>(null);

  useEffect(() => {
    let cancelled = false;
    void repositories()
      .documents.list()
      .then((documents) => {
        if (cancelled) return;
        // Only work with ink in it. An empty document the app minted on open is
        // not somewhere anybody wants to be sent back to.
        const withInk = documents.find((document) => document.payload.strokes.length > 0);
        if (withInk) {
          setRecent({ id: withInk.id, title: withInk.title, updatedAt: withInk.updatedAt });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="home">
      <section className="home__hero" aria-labelledby="home-title">
        <div className="home__intro">
          <p className="eyebrow">Your harmony workbench</p>
          <h1 className="home__title" id="home-title">Put theory under your pencil.</h1>
          <p className="home__tagline">Write it. See it. Hear it.</p>
          <p className="home__scope">
            Move from a handwritten idea to clear notation, the right name, and the sound—all in
            one place.
          </p>
        </div>

        <button type="button" className="home__primary" onClick={() => go('write')}>
          <span className="home__primary-topline">
            <span className="home__primary-icon" aria-hidden="true"><Icon name="pen" size={18} /></span>
            <span>Manuscript</span>
          </span>
          <span className="home__primary-title">Open the staff</span>
          <span className="home__primary-blurb">Draw a chord with Apple Pencil. MusicMentor reads, engraves, names, and plays it.</span>
          <span className="home__staff-preview" aria-hidden="true">
            <i /><i /><i /><i /><i />
            <b className="home__note home__note--one" />
            <b className="home__note home__note--two" />
            <b className="home__note home__note--three" />
          </span>
          <span className="home__primary-action">Start writing <Icon name="arrowRight" size={17} /></span>
        </button>
      </section>

      {recent ? (
        <button type="button" className="home__continue" onClick={() => onContinue(recent.id)}>
          <span className="home__continue-label">Continue</span>
          <span className="home__continue-title">{recent.title || 'Untitled'}</span>
          <span className="home__continue-meta">{describeWhen(recent.updatedAt)}</span>
        </button>
      ) : null}

      <section className="home__explore" aria-labelledby="home-explore-title">
        <div className="home__section-head">
          <div>
            <p className="eyebrow">Choose a direction</p>
            <h2 id="home-explore-title">Study with intention.</h2>
          </div>
          <p>Build, hear, practise, or bring the work into class.</p>
        </div>
        <div className="home__cards">
          {CARDS.map((card) => (
            <button
              key={card.destination}
              type="button"
              className="home__card"
              onClick={() => go(card.destination)}
            >
              <span className="home__card-icon" aria-hidden="true">
                <Icon name={card.icon} size={20} />
              </span>
              <span className="home__card-copy">
                <span className="home__card-title">{card.title}</span>
                <span className="home__card-blurb">{card.blurb}</span>
              </span>
              <Icon name="arrowRight" size={17} className="home__card-arrow" />
            </button>
          ))}
        </div>
      </section>

      <p className="home__privacy">
        Guest work stays on this iPad. Sign in only when you want your work available across
        devices or shared with a class.
      </p>
    </div>
  );
}

function describeWhen(timestamp: number): string {
  const days = Math.floor((Date.now() - timestamp) / 86_400_000);
  if (days <= 0) return 'edited today';
  if (days === 1) return 'edited yesterday';
  if (days < 30) return `edited ${days} days ago`;
  return `edited ${new Date(timestamp).toLocaleDateString()}`;
}
